import type { RfpFullQuestion, RfpFullSection } from "../types/rfp";

export const countSyncEligibleQuestions = (
  sections: readonly RfpFullSection[],
): number =>
  sections.reduce(
    (count, section) =>
      count +
      section.questions.filter(
        (question) => question.status === "pending" || question.status === "error",
      ).length,
    0,
  );

export const isQuestionAssignedToUser = (
  assignedTo: string | null | undefined,
  userId: string | null | undefined,
): boolean => {
  const normalizedAssignee = assignedTo?.trim().toLowerCase() ?? "";
  const normalizedUserId = userId?.trim().toLowerCase() ?? "";
  if (!normalizedAssignee || !normalizedUserId) {
    return false;
  }
  return normalizedAssignee === normalizedUserId;
};

export const filterRfpQuestionsByAssignee = (
  questions: readonly RfpFullQuestion[],
  userId: string | null | undefined,
): RfpFullQuestion[] =>
  questions.filter((question) => isQuestionAssignedToUser(question.assigned_to, userId));

export const filterRfpQuestionsBySearch = (
  questions: readonly RfpFullQuestion[],
  searchInput: string,
): RfpFullQuestion[] => {
  const query = searchInput.trim().toLowerCase();
  if (!query) {
    return [...questions];
  }
  return questions.filter((question) =>
    (question.better_question || question.raw_question).toLowerCase().includes(query),
  );
};

export type RfpSidebarSectionView = {
  readonly name: string;
  readonly section_sort_order?: number;
  readonly total: number;
  readonly completed: number;
};

const toSidebarSectionView = (
  section: RfpFullSection,
  total: number,
  completed: number,
): RfpSidebarSectionView => ({
  name: section.name,
  ...(section.section_sort_order !== undefined
    ? { section_sort_order: section.section_sort_order }
    : {}),
  total,
  completed,
});

export const buildSidebarSections = (
  sections: readonly RfpFullSection[],
  options: {
    readonly assignedToMeOnly: boolean;
    readonly currentUserId: string | null | undefined;
  },
): readonly RfpSidebarSectionView[] => {
  const { assignedToMeOnly, currentUserId } = options;

  if (!assignedToMeOnly || !currentUserId?.trim()) {
    return sections.map((section) =>
      toSidebarSectionView(section, section.total, section.completed),
    );
  }

  return sections.flatMap((section) => {
    const assignedQuestions = filterRfpQuestionsByAssignee(section.questions, currentUserId);
    if (assignedQuestions.length === 0) {
      return [];
    }
    const completed = assignedQuestions.filter((question) => question.selected_answer != null).length;
    return [toSidebarSectionView(section, assignedQuestions.length, completed)];
  });
};

export const computeAssignmentProgress = (
  sections: readonly RfpFullSection[],
  currentUserId: string | null | undefined,
): { readonly completed: number; readonly total: number; readonly percentage: number } => {
  const assigned = sections.flatMap((section) =>
    filterRfpQuestionsByAssignee(section.questions, currentUserId),
  );
  const total = assigned.length;
  const completed = assigned.filter((question) => question.selected_answer != null).length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, total, percentage };
};

export const findFirstSectionWithAssignments = (
  sections: readonly RfpFullSection[],
  currentUserId: string | null | undefined,
): string | null => {
  for (const section of sections) {
    if (section.questions.some((question) => isQuestionAssignedToUser(question.assigned_to, currentUserId))) {
      return section.name;
    }
  }
  return null;
};
